"use client";
import { createContext, useContext, useEffect, useState } from "react";
import { USER_THEMES } from "@/config/enums/user.enum";

const initialState = {
  theme: USER_THEMES.SYSTEM,
  setTheme: () => null
};

const ThemeProviderContext = createContext(initialState);

export function ThemeProvider({
  children,
  defaultTheme = USER_THEMES.DARK,
  storageKey = "vite-ui-theme",
  ...props
}) {
  // Normalize theme value from localStorage (handle old lowercase values)
  const normalizeTheme = (themeValue) => {
    if (!themeValue) return USER_THEMES.DARK;
    
    // Map old lowercase values to new uppercase enum values
    const themeMap = {
      "light": USER_THEMES.LIGHT,
      "dark": USER_THEMES.DARK,
      "system": USER_THEMES.SYSTEM,
    };
    
    // If it's already in the new format, return as is
    if (Object.values(USER_THEMES).includes(themeValue)) {
      return themeValue;
    }
    
    // Otherwise, try to map from old format
    return themeMap[themeValue] || USER_THEMES.DARK;
  };
  
  const [theme, setTheme] = useState(normalizeTheme(defaultTheme));

  // Load theme from localStorage on client
  useEffect(() => {
    const storedTheme = localStorage.getItem(storageKey);
    if (storedTheme) {
      setTheme(normalizeTheme(storedTheme));
    }
  }, [storageKey]);

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement;

    // Map uppercase enum values to lowercase CSS class names
    const themeToClass = {
      [USER_THEMES.LIGHT]: "light",
      [USER_THEMES.DARK]: "dark",
      [USER_THEMES.SYSTEM]: "system",
    };

    // Remove all theme classes
    root.classList.remove("light", "dark", "system");

    if (theme === USER_THEMES.SYSTEM) {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? USER_THEMES.DARK
        : USER_THEMES.LIGHT;

      root.classList.add(themeToClass[systemTheme] || "dark");
      return;
    }

    // Add the appropriate CSS class (lowercase)
    const cssClass = themeToClass[theme] || theme?.toLowerCase() || "dark";
    root.classList.add(cssClass);
  }, [theme]);

  const value = {
    theme,
    setTheme: (newTheme) => {
      localStorage.setItem(storageKey, newTheme);
      setTheme(newTheme);
    }
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};
